import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../api/client';
import BookViewer, { buildFullBookPages } from '../components/BookViewer';
import BookDownload from '../components/BookDownload';
import FinalizeLoader from '../components/FinalizeLoader';
import { useT } from '../i18n';

export default function FinalizeOrder() {
  const t = useT();
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [finalizingImages, setFinalizingImages] = useState(false);
  const [changes, setChanges] = useState('');
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Phase 1: fast initial fetch (order + story). Keep `loading=true` only
      // for this — it's <1s and a plain spinner is fine.
      let storyData = null;
      let shareToken = null;
      try {
        const orderData = await api.get(`/api/orders/${id}`);
        if (cancelled) return;
        setOrder(orderData);
        shareToken = orderData.items?.[0]?.storyId?.shareToken;
        if (shareToken) {
          storyData = await api.get(`/api/stories/preview/${shareToken}`);
          if (cancelled) return;
          setStory(storyData);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message);
          setLoading(false);
        }
        return;
      }
      if (cancelled) return;
      setLoading(false);

      // Phase 2: finalize remaining illustrations (slow — 30-120s). Render
      // the rich finalize loader while this runs by flipping `finalizingImages`.
      // Preview-cap rule: the saved story only has cover + scene[0] illustrated;
      // the rest fill in here, gated server-side on a paid Order existing.
      const needsFinalize = (storyData?.scenes || []).some((s) => !s.imageUrl);
      if (!needsFinalize || !shareToken) return;
      setFinalizingImages(true);
      try {
        await api.post(`/api/stories/${shareToken}/finalize-illustrations`, {});
        const refreshed = await api.get(`/api/stories/preview/${shareToken}`);
        if (!cancelled) setStory(refreshed);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setFinalizingImages(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const pages = useMemo(() => (story ? buildFullBookPages(story, t) : []), [story, t]);

  const handleApprove = async () => {
    setApproving(true);
    setError('');
    try {
      await api.post(`/api/orders/${id}/approve`);
      navigate(`/account/orders/${id}`);
    } catch (e) {
      setError(e.message);
      setApproving(false);
    }
  };

  if (loading) return <div className="loader"><div className="loader-spinner" /></div>;
  if (error || !order) return (
    <div className="error-card">
      <h2>{t.errorTitle}</h2>
      <p>{error || ''}</p>
      <Link to="/account" className="submit-btn">{t.myStories}</Link>
    </div>
  );

  // While we're filling in the post-purchase illustrations, swap the page
  // contents for the rich finalize loader — the generic spinner doesn't
  // explain what's happening and people thought their order was broken.
  if (finalizingImages) return <FinalizeLoader />;

  return (
    <div className="finalize-page">
      <div className="finalize-header">
        <h1 className="page-title">{t.finalizePageTitle}</h1>
        <p className="finalize-subtitle">{t.finalizePageSubtitle}</p>
      </div>

      {pages.length > 0 ? (
        <BookViewer pages={pages} font={story.storyFont} />
      ) : (
        <p className="empty-state">{t.templateNotFound}</p>
      )}

      {story && pages.length > 0 && (
        <div className="finalize-download">
          <BookDownload story={story} />
        </div>
      )}

      <div className="finalize-actions">
        <label className="field">
          <span className="field-label">{t.requestChangesLabel}</span>
          <textarea
            className="finalize-textarea"
            rows={4}
            value={changes}
            onChange={(e) => setChanges(e.target.value)}
            placeholder={t.requestChangesPlaceholder}
          />
        </label>

        {order.approvedAt ? (
          <div className="finalize-approved-inline">
            {t.finalizeApprovedTitle}
          </div>
        ) : (
          <button
            type="button"
            className="submit-btn"
            onClick={handleApprove}
            disabled={approving}
          >
            {approving ? t.approving : t.approveAndPrint}
          </button>
        )}

        <Link to={`/account/orders/${id}`} className="finalize-back-link">
          {t.backToOrder}
        </Link>

        {error && <p className="auth-error">{error}</p>}
      </div>
    </div>
  );
}
